<script setup lang="ts">
import { useHead } from '@unhead/vue';
import AppImage from '@/components/shared/AppImage.vue';
import Layout from '@/layout/Layout.vue';
import locations from '@/scripts/locations';

useHead({
  title: "Albano's Villa | Locations",
  meta: [
    {
      name: 'description',
      content:
        "Find Albano's Villa in Michigan City and LaPorte, Indiana — three locations with addresses, phone numbers and directions to each restaurant.",
    },
  ],
});

const [michigan, laporte, johnson] = locations;

/**
 * Strip non-digits from a phone string for a `tel:` href (e.g. "(219) 872-0571" → "+12198720571").
 */
function telHref(phone: string): string {
  return `tel:+1${phone.replace(/\D/g, '')}`;
}
</script>

<template>
  <Layout main-class="mx-auto grid grid-cols-1 *:relative *:text-center md:grid-cols-3">
    <h1 class="sr-only col-span-full">Locations</h1>
    <div class="mx-auto w-fit max-md:pb-50">
      <h2 class="text-18 absolute inset-x-0 mt-5 text-white">{{ michigan.name }}</h2>
      <AppImage
        name="loc-michigan-city"
        alt="Michigan City location"
        sizes="(min-width: 800px) 300px, 90vw"
      />
      <div class="*:flex *:flex-col">
        <p>
          <a :href="michigan.mapUrl" target="_blank" rel="noopener" class="flex flex-col">
            <span>{{ michigan.address }}</span>
            <span>{{ michigan.city }}</span>
            <span class="sr-only"> (opens in new tab)</span>
          </a>
        </p>
        <p>
          <a :href="telHref(michigan.phone)">{{ michigan.phone }}</a>
        </p>
        <p>
          <strong>HOURS:</strong>
          <span>Tues - Sun: Open Daily 4:00pm</span>
        </p>
        <p>Dine-in and Carry-out</p>
      </div>
    </div>
    <div class="mx-auto w-fit max-md:pb-50">
      <h2 class="text-18 absolute inset-x-0 mt-5 text-white">{{ laporte.name }}</h2>
      <AppImage
        name="loc-la-porte"
        alt="La Porte location"
        sizes="(min-width: 800px) 300px, 90vw"
      />
      <div class="*:flex *:flex-col">
        <p>
          <a :href="laporte.mapUrl" target="_blank" rel="noopener" class="flex flex-col">
            <span>{{ laporte.address }}</span>
            <span>{{ laporte.city }}</span>
            <span class="sr-only"> (opens in new tab)</span>
          </a>
        </p>
        <p>
          <a :href="telHref(laporte.phone)">{{ laporte.phone }}</a>
        </p>
        <p>
          <strong>HOURS:</strong>
          <span>Tues - Sun: Open Daily 4:00pm</span>
        </p>
        <p>
          <span>Dine-in and Carry-out</span>
          <span>Drive-thru pickup window</span>
          <span>Patio seating</span>
        </p>
      </div>
    </div>
    <div class="mx-auto w-fit max-md:pb-50">
      <h2 class="text-18 absolute inset-x-0 mt-5 text-white">{{ johnson.name }}</h2>
      <AppImage
        name="loc-johnsons-road"
        alt="Johnson Road location"
        sizes="(min-width: 800px) 300px, 90vw"
      />
      <div class="*:flex *:flex-col">
        <p>
          <a :href="johnson.mapUrl" target="_blank" rel="noopener" class="flex flex-col">
            <span>{{ johnson.address }}</span>
            <span>{{ johnson.city }}</span>
            <span class="sr-only"> (opens in new tab)</span>
          </a>
        </p>
        <p>
          <a :href="telHref(johnson.phone)">{{ johnson.phone }}</a>
        </p>
        <p>
          <strong>HOURS:</strong>
          <span>Open Daily 3:00pm</span>
        </p>
        <p>
          <span>Carry-out ONLY</span>
          <span>Banquet room available for</span>
          <span>private parties</span>
          <span>Kid's make-your-own</span>
          <span>pizza parties</span>
        </p>
      </div>
    </div>
  </Layout>
</template>
